import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, X, MapPin, Users, Clock, Link2, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function EventModeration() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/events/pending");
      setPending(data);
    } catch {
      setPending([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/events/${id}/${action}`, {});
      toast.success(action === "approve" ? "Event approved & published" : "Event rejected");
      setPending((p) => p.filter((e) => e.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  if (!loading && pending.length === 0) return null;

  return (
    <Card className="p-4 sm:p-5 mb-6 border-l-4 border-indigo-500 bg-indigo-50/40" data-testid="event-moderation">
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays size={18} className="text-indigo-600" />
        <p className="font-semibold text-sm">Event submissions awaiting approval</p>
        <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-bold" data-testid="pending-events-count">
          {pending.length}
        </span>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-3">
          {pending.map((e) => (
            <div key={e.id} className="rounded-lg border border-indigo-200 bg-white p-3 sm:p-4" data-testid={`pending-event-${e.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                {e.image_url && (
                  <img src={e.image_url.startsWith("/api/") ? `${API}${e.image_url}` : e.image_url} alt="" className="w-full sm:w-28 h-28 rounded-md object-cover border border-indigo-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[var(--ink)]">{e.title}</p>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><Clock size={12} className="text-indigo-500" /> {fmtDate(e.start_datetime)}</span>
                    <span className="inline-flex items-center gap-1.5 truncate"><MapPin size={12} className="text-indigo-500" /> {e.location}</span>
                    <span className="inline-flex items-center gap-1.5"><Users size={12} className="text-indigo-500" /> {e.audience}</span>
                    {e.category && <span className="inline-flex items-center gap-1.5"><Info size={12} className="text-indigo-500" /> {e.category}{e.expected_attendance ? ` · ~${e.expected_attendance}` : ""}</span>}
                    {e.website_url && <a href={e.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-indigo-600 hover:underline truncate"><Link2 size={12} /> {e.website_url}</a>}
                  </div>
                  {e.description && <p className="mt-1.5 text-xs text-gray-700 line-clamp-3">{e.description}</p>}
                  <p className="mt-1.5 text-[11px] text-gray-400">Submitted by {e.full_name} · {e.email} · {e.phone}</p>
                </div>
                <div className="flex sm:flex-col gap-2 shrink-0">
                  <Button size="sm" className="btn-brand flex-1" disabled={busy === e.id} onClick={() => act(e.id, "approve")} data-testid={`approve-event-${e.id}`}>
                    <CheckCircle2 size={13} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 flex-1" disabled={busy === e.id} onClick={() => act(e.id, "reject")} data-testid={`reject-event-${e.id}`}>
                    <X size={13} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
